import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import '@shoelace-style/shoelace/dist/components/input/input.js';
import { styles as sharedStyles } from '../styles/shared-styles';
import { money } from '../datatypes/currency';
import { IDenominationCount } from '../api/model/requests';

// J3 — the cash-count grid used when starting (opening float) and ending
// (counted close) a shift. One row per denomination of the active shop currency
// (notes + coins, high -> low); the cashier types how many of each are in the
// drawer and the grid shows each line subtotal + a live grand total = Σ(denom ×
// count). That total is the float / counted-close; the lines are sent so the
// Z-report + audit can show the breakdown.
//
// Self-contained: it owns the entered counts. The host reads `.counts` (non-zero
// lines, for the request) and `.total` (the derived money value) at submit time,
// and calls `.reset()` when (re)opening the dialog. The registry only offers
// valid denominations, so there is no membership validation here.
@customElement('denomination-count-grid')
export class DenominationCountGrid extends LitElement {
  // Denominations to count, high -> low (from currencyDenominations()).
  @property({ type: Array }) denominations: number[] = [];

  // Entered count per denomination, keyed by denomination value. Stored as the
  // raw input string so a blank field stays blank (distinct from an entered 0).
  @state() private _counts = new Map<number, string>();

  static styles = [
    sharedStyles,
    css`
      :host {
        display: block;
      }
      .grid {
        display: grid;
        grid-template-columns: auto 5.5rem 1fr;
        gap: 0.4rem 0.75rem;
        align-items: center;
      }
      .denom {
        font-weight: 600;
        text-align: right;
        white-space: nowrap;
      }
      .sub {
        text-align: right;
        color: var(--sl-color-neutral-600);
        font-variant-numeric: tabular-nums;
      }
      .count-input {
        width: 5.5rem;
      }
      /* Every count box carries a real label ("How many PHP500") so it has an
         accessible name - without one a screen-reader user counting a till hears
         a column of unnamed number boxes, and nothing in a test can name them
         either. The denomination is already printed beside it for a sighted
         operator, so the rendered label is hidden rather than shown twice; the
         visible column is aria-hidden so the name is not announced twice. */
      .count-input::part(form-control-label) {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0 0 0 0);
        white-space: nowrap;
        border: 0;
      }
      .total {
        display: flex;
        justify-content: space-between;
        align-items: baseline;
        margin-top: 0.75rem;
        padding-top: 0.5rem;
        border-top: 1px solid var(--sl-color-neutral-200);
        font-weight: 600;
      }
      .total .amount {
        font-size: 1.125rem;
        font-variant-numeric: tabular-nums;
      }
    `,
  ];

  // The derived money value of the entered counts = Σ(denom × count).
  get total(): number {
    let t = 0;
    for (const d of this.denominations) {
      const c = parseInt(this._counts.get(d) ?? '', 10);
      if (!Number.isNaN(c) && c > 0) t += d * c;
    }
    return t;
  }

  // The non-zero count lines, for the request payload.
  get counts(): IDenominationCount[] {
    const out: IDenominationCount[] = [];
    for (const d of this.denominations) {
      const c = parseInt(this._counts.get(d) ?? '', 10);
      if (!Number.isNaN(c) && c > 0) out.push({ denomination: d, count: c });
    }
    return out;
  }

  // Clear every entered count. The host calls this when (re)opening the dialog.
  reset(): void {
    this._counts = new Map();
  }

  private _set(denom: number, value: string): void {
    const next = new Map(this._counts);
    next.set(denom, value);
    this._counts = next;
    this.dispatchEvent(
      new CustomEvent('counts-changed', {
        detail: { counts: this.counts, total: this.total },
        bubbles: true,
        composed: true,
      })
    );
  }

  private _lineSubtotal(denom: number): number {
    const c = parseInt(this._counts.get(denom) ?? '', 10);
    return !Number.isNaN(c) && c > 0 ? denom * c : 0;
  }

  render() {
    return html`
      <div class="grid">
        ${this.denominations.map(
          (d) => html`
            <span class="denom" aria-hidden="true">${money(d)}</span>
            <sl-input
              class="count-input"
              label=${`How many ${money(d)}`}
              type="number"
              inputmode="numeric"
              step="1"
              min="0"
              placeholder="0"
              .value=${this._counts.get(d) ?? ''}
              @sl-input=${(e: Event) => this._set(d, (e.target as HTMLInputElement).value)}
            ></sl-input>
            <span class="sub">${money(this._lineSubtotal(d))}</span>
          `
        )}
      </div>
      <div class="total">
        <span>Total counted</span>
        <span class="amount">${money(this.total)}</span>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'denomination-count-grid': DenominationCountGrid;
  }
}
